import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, ClipboardList, Wallet, User, Headphones } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/", label: "Home", icon: Home, id: "home", end: true },
  { to: "/tasks", label: "Tasks", icon: ClipboardList, id: "tasks" },
  { to: "/payments", label: "Payments", icon: Wallet, id: "payments" },
  { to: "/account", label: "Account", icon: User, id: "account" },
  { to: "/support", label: "Support", icon: Headphones, id: "support" },
];

export default function MainLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const loggedIn = !!user; // null = still loading, false = logged out

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Brand />
          <div className="flex items-center gap-2.5">
            {loggedIn ? (
              <>
                <NotificationBell />
              <button onClick={() => navigate("/account")} data-testid="header-user-chip"
                className="flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
                <span className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{user.name?.[0]?.toUpperCase()}</span>
                <span className="text-sm font-semibold text-slate-800 max-w-[120px] truncate">{user.name}</span>
              </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn" className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors">Login</Link>
                <Link to="/register" data-testid="nav-register-btn" className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm">Register</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-8">
        {loggedIn && (
          <aside className="hidden lg:block w-56 shrink-0 py-6" data-testid="desktop-sidebar">
            <nav className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-2.5 space-y-1 sticky top-24">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.id}`}
                  className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-700 hover:bg-slate-100"}`}>
                  <n.icon className="w-[18px] h-[18px]" /> {n.label}
                </NavLink>
              ))}
            </nav>
          </aside>
        )}
        <main className="flex-1 min-w-0 py-6 sm:py-8 pb-24 lg:pb-10">
          <Outlet />
        </main>
      </div>

      {loggedIn && (
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_16px_-6px_rgba(15,23,42,0.1)]" data-testid="bottom-nav">
          <div className="grid grid-cols-5">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bottom-nav-${n.id}`}
                className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold ${isActive ? "text-blue-600" : "text-slate-500"}`}>
                <n.icon className="w-5 h-5" />
                {n.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}

      {!loggedIn && (
        <footer className="bg-slate-900 text-slate-300 mt-12" data-testid="public-footer">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div>
              <Brand dark />
              <p className="mt-4 text-sm text-slate-400 leading-relaxed">Test Apps. Share Feedback. Get Rewarded.</p>
              <p className="mt-2 text-xs text-slate-500">Equinox 4 Review is an independent app-testing research platform.</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider">Platform</h4>
              <ul className="mt-3 space-y-2 text-sm">
                <li><Link to="/tasks" data-testid="footer-link-tasks" className="hover:text-white transition-colors">Available Tasks</Link></li>
                <li><Link to="/support" data-testid="footer-link-support" className="hover:text-white transition-colors">Support & FAQ</Link></li>
                <li><Link to="/register" data-testid="footer-link-register" className="hover:text-white transition-colors">Create Account</Link></li>
                <li><Link to="/admin/login" data-testid="footer-link-admin" className="hover:text-white transition-colors">Admin Login</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider">Our Promise</h4>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">We never ask for a specific star rating or a positive review. Rewards are for completing research tasks and sharing honest feedback — positive, neutral, or negative.</p>
            </div>
          </div>
          <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-500">© {new Date().getFullYear()} Equinox 4 Review · Equinox4Review.in · All rights reserved.</div>
        </footer>
      )}
    </div>
  );
}
